import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { AdminHttpClient } from '../core/admin-http-client.service';
import { AdminDeletionRecord, AdminGameDetail, AdminGameListItem, AdminRole, AdminStatus, AdminUser } from '../core/models';

export interface AdminUserInput {
  email: string;
  name: string;
  status: AdminStatus;
  permissions: AdminRole;
}

@Injectable({ providedIn: 'root' })
export class AdminApiService {
  private readonly http = inject(AdminHttpClient);

  getActiveGames(startDate: string, endDate: string): Observable<AdminGameListItem[]> {
    const params = new HttpParams().set('startDate', startDate).set('endDate', endDate);
    return this.http.get<{ games: AdminGameListItem[] }>('/admin-api/games/active', params).pipe(
      map(body => body.games ?? []),
    );
  }

  getAdminGame(gameId: string): Observable<AdminGameDetail> {
    return this.http.get<AdminGameDetail>(`/admin-api/games/${encodeURIComponent(gameId)}`);
  }

  deleteAdminGame(gameId: string, reason: string): Observable<void> {
    return this.http.post<void>(
      `/admin-api/games/${encodeURIComponent(gameId)}/delete`,
      { requestBody: { reason } },
    );
  }

  restoreAdminGame(gameId: string): Observable<void> {
    return this.http.post<void>(
      `/admin-api/games/${encodeURIComponent(gameId)}/restore`,
      null,
    );
  }

  getDeletionRecords(startDate: string, endDate: string, removedByType: string): Observable<AdminDeletionRecord[]> {
    const params = new HttpParams()
      .set('startDate', startDate)
      .set('endDate', endDate)
      .set('removedByType', removedByType);
    return this.http.get<{ records: AdminDeletionRecord[] }>('/admin-api/games/admin-deletion-records', params).pipe(
      map(body => body.records ?? []),
    );
  }

  getAdminUsers(): Observable<AdminUser[]> {
    return this.http.get<{ adminUsers: AdminUser[] }>('/admin-api/admin-users').pipe(
      map(body => body.adminUsers ?? []),
    );
  }

  createAdminUser(input: AdminUserInput): Observable<void> {
    return this.http.post<void>('/admin-api/admin-users', { requestBody: input });
  }

  updateAdminUser(adminId: string, input: Omit<AdminUserInput, 'email'>): Observable<void> {
    return this.http.post<void>(
      `/admin-api/admin-users/${encodeURIComponent(adminId)}`,
      { requestBody: input },
    );
  }
}
